import { Download, Pause, Play } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { getMediaUrl } from '../api';
import { downloadCrossOrigin } from '../lib/download-blob';
import { WaveformBars } from './WaveformBars';

interface Props {
  /** ID da msg — signed URL é buscada SÓ no primeiro click no play. */
  messageId: number;
  /** Waveform pré-calculada (32 floats 0..1). Null = padrão neutro. */
  waveform?: number[] | null;
  /** Duração em ms (do servidor). Visível antes do <audio> existir. */
  durationMs?: number | null;
}

const SPEEDS = [1, 1.5, 2] as const;
type Speed = (typeof SPEEDS)[number];

/**
 * Render instantâneo: <WaveformBars> + duração do servidor. O <audio> é
 * instanciado SOMENTE no primeiro click no play (lazy 100%, igual ao
 * AtenderBem). Antes disso, zero requests de mídia. Streaming via Range
 * request quando o user toca play.
 */
export function AudioPlayer({ messageId, waveform, durationMs }: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [loadingUrl, setLoadingUrl] = useState(false);
  // Duração vinda do <audio> via loadedmetadata (só populada após primeiro play).
  // Tem prioridade sobre `durationMs` da prop quando disponível (mais precisa que
  // o valor do servidor — Meta às vezes arredonda pra segundo inteiro).
  const [loadedDuration, setLoadedDuration] = useState<number | null>(null);
  // Duração derivada: <audio> loadedmetadata > prop do servidor > null.
  // Derivar (em vez de useState com `durationMs` como initial) garante que
  // mudanças tardias na prop (optimistic null → refetch com valor real) sejam
  // refletidas — useState só lê o initial no primeiro mount.
  const duration =
    loadedDuration ?? (durationMs && durationMs > 0 ? durationMs / 1000 : null);
  const [position, setPosition] = useState(0);
  const [speed, setSpeed] = useState<Speed>(1);

  // Cleanup ao desmontar — para playback e libera o elemento.
  useEffect(() => {
    return () => {
      const a = audioRef.current;
      if (a) {
        a.pause();
        a.src = '';
      }
      audioRef.current = null;
    };
  }, []);

  // Aplica velocidade quando muda (só se o <audio> já foi instanciado).
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.playbackRate = speed;
    a.preservesPitch = true;
  }, [speed]);

  const ensureAudio = async (): Promise<HTMLAudioElement> => {
    if (audioRef.current) return audioRef.current;
    setLoadingUrl(true);
    try {
      const { url } = await getMediaUrl(messageId);
      const a = new Audio(url);
      a.preload = 'auto';
      a.playbackRate = speed;
      a.preservesPitch = true;
      a.addEventListener('timeupdate', () => setPosition(a.currentTime));
      a.addEventListener('loadedmetadata', () => {
        if (Number.isFinite(a.duration)) setLoadedDuration(a.duration);
      });
      a.addEventListener('ended', () => {
        setPlaying(false);
        setPosition(0);
      });
      audioRef.current = a;
      return a;
    } finally {
      setLoadingUrl(false);
    }
  };

  const handleToggle = async (): Promise<void> => {
    const a = await ensureAudio();
    if (a.paused) {
      await a.play();
      setPlaying(true);
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  const handleSeek = async (ratio: number): Promise<void> => {
    if (!duration) return;
    const a = await ensureAudio();
    a.currentTime = ratio * duration;
    setPosition(a.currentTime);
  };

  const cycleSpeed = (): void => {
    const idx = SPEEDS.indexOf(speed);
    const next = SPEEDS[(idx + 1) % SPEEDS.length] ?? 1;
    setSpeed(next);
  };

  const [downloading, setDownloading] = useState(false);
  const handleDownload = async (): Promise<void> => {
    setDownloading(true);
    try {
      const { url, mime_type } = await getMediaUrl(messageId);
      const ext = mime_type.includes('opus') || mime_type.includes('ogg')
        ? 'ogg'
        : mime_type.includes('mp3') || mime_type.includes('mpeg')
          ? 'mp3'
          : mime_type.includes('wav')
            ? 'wav'
            : 'audio';
      await downloadCrossOrigin(url, `audio-${messageId}.${ext}`);
    } catch (err) {
      console.warn('audio download failed', err);
    } finally {
      setDownloading(false);
    }
  };

  const speedLabel = `${speed % 1 === 0 ? speed.toFixed(0) : speed}x`;
  const progress = duration ? Math.min(1, position / duration) : 0;

  return (
    <div className="flex items-center gap-1.5">
      <div className="flex w-[240px] items-center gap-2 rounded-md bg-white/40 p-2 dark:bg-n-100">
        <button
          type="button"
          onClick={handleToggle}
          disabled={loadingUrl}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-pill bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-70"
          aria-label={playing ? 'Pausar áudio' : 'Reproduzir áudio'}
        >
          {playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
        </button>
        <WaveformBars
          amplitudes={waveform}
          progress={progress}
          onSeek={(r) => void handleSeek(r)}
        />
        <span className="font-mono text-[10px] text-n-600 tabular-nums">
          {duration ? formatTime(playing ? position : duration) : '--:--'}
        </span>
        <button
          type="button"
          onClick={cycleSpeed}
          title={`Velocidade: ${speedLabel} — clique pra alternar`}
          aria-label={`Velocidade ${speedLabel}, clique pra alternar`}
          className="flex h-6 min-w-[34px] items-center justify-center rounded-pill bg-n-200/70 px-1.5 font-mono text-[10px] font-semibold text-n-700 tabular-nums transition hover:bg-n-300 active:bg-n-400"
        >
          {speedLabel}
        </button>
      </div>
      <button
        type="button"
        onClick={() => void handleDownload()}
        disabled={downloading}
        title="Baixar áudio"
        aria-label="Baixar áudio"
        className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-pill text-n-600 transition hover:bg-black/5 hover:text-n-800 disabled:opacity-50"
      >
        <Download size={14} />
      </button>
    </div>
  );
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
